var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Component } from "component-lifecycle";
import { DOMBuilder } from "./core/DOMBuilder";
import { OptionResolver } from "./core/OptionResolver";
import { StateReducer } from "./core/StateReducer";
import { ToggleActionType, ToggleStateValue } from "./core/StateReducer.types";
import ToggleEvents from "./types/ToggleEvents";
export class Toggle extends Component {
    /**
     * Initializes a new instance of the Toggle component.
     *
     * @param element The checkbox element backing the toggle.
     * @param options User-provided options.
     */
    constructor(element, options) {
        super(element, { bubbleEvents: true });
        this.PREFIX = "toggle";
        this.pointer = null;
        this.SCROLL_THRESHOLD = 10;
        this.suppressExternalSync = false;
        this.originalDescriptors = new Map();
        /**
         * Handles the change event of the input element of the toggle.
         * This event listener is responsible for detecting when the input element
         * of the toggle changes its state and triggering the update method to keep the toggle in sync.
         */
        this.onExternalChange = () => {
            this.update();
        };
        this.onFormReset = () => {
            setTimeout(() => this.onExternalChange(), 0);
        };
        /**
         * Handles pointer down events by initiating the toggle action and setting up
         * listeners for pointer movement, release, and cancellation.
         *
         * The method early exits if:
         * - the pointer event is not a primary mouse button click
         * - the toggle cannot be interacted with (`disabled` or `readonly`)
         * @param e The PointerEvent object representing the pointer down event.
         */
        this.onPointerDown = (e) => {
            if (e.pointerType === "mouse" && e.button !== 0)
                return;
            if (!this.stateReducer.canInteract())
                return;
            this.pointer = { x: e.clientX, y: e.clientY };
            this.domBuilder.root.addEventListener("pointermove", this.onPointerMove, {
                passive: true,
            });
            this.domBuilder.root.addEventListener("pointerup", this.onPointerUp, {
                passive: true,
            });
            this.domBuilder.root.addEventListener("pointercancel", this.onPointerCancel, { passive: true });
        };
        /**
         * Handles pointer move events by checking the distance moved from the initial pointer down position.
         * If the pointer has moved beyond a certain threshold, the pointer interaction is cancelled.
         *
         * Allows dragging within the width of the toggle but cancels if vertical movement exceeds the scroll threshold.
         * @param e The PointerEvent object representing the pointer move event.
         */
        this.onPointerMove = (e) => {
            const dx = Math.abs(e.clientX - this.pointer.x);
            const dy = Math.abs(e.clientY - this.pointer.y);
            if (dy > this.SCROLL_THRESHOLD || dx > this.domBuilder.root.offsetWidth) {
                this.onPointerCancel();
            }
        };
        /**
         * Handles pointer up events by determining if the pointer interaction
         * should trigger a toggle action based on the distance moved.
         *
         * If the pointer has moved beyond a certain threshold, the pointer interaction is cancelled.
         * Allows dragging within the width of the toggle but cancels if vertical movement exceeds the scroll threshold.
         * Finally, it cleans up by calling the pointer cancel handler.
         *
         * If the pointer event is not a primary mouse button click, the interaction is cancelled.
         * @param e The PointerEvent object representing the pointer up event.
         */
        this.onPointerUp = (e) => {
            if (e.pointerType === "mouse" && e.button !== 0) {
                this.onPointerCancel();
                return;
            }
            const dx = Math.abs(e.clientX - this.pointer.x);
            const dy = Math.abs(e.clientY - this.pointer.y);
            if (dy <= this.SCROLL_THRESHOLD && dx <= this.domBuilder.root.offsetWidth) {
                this.apply(ToggleActionType.NEXT);
            }
            this.onPointerCancel();
        };
        /**
         * Cleans up pointer event listeners after a pointer interaction is completed or cancelled.
         *
         * This method removes the `pointermove`, `pointerup`, and `pointercancel` event listeners
         * from the root element of the toggle.
         * However, `pointerdown` listener remains active for future interactions.
         */
        this.onPointerCancel = () => {
            this.domBuilder.root.removeEventListener("pointermove", this.onPointerMove);
            this.domBuilder.root.removeEventListener("pointerup", this.onPointerUp);
            this.domBuilder.root.removeEventListener("pointercancel", this.onPointerCancel);
        };
        this.handlerKeyboardEvent = (e) => {
            if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                this.apply(ToggleActionType.NEXT);
            }
        };
        this.handlerLabelEvent = (e) => {
            e.preventDefault();
            this.apply(ToggleActionType.NEXT);
            this.domBuilder.root.focus();
        };
        this.userOptions = options;
        this.toggleOptions = OptionResolver.resolve(element, options);
        this.element.bsToggle = this;
    }
    /**
     * Initializes internal state. No DOM access, no side effects.
     * @returns {Promise<{ cancelled: boolean }>} transition result
     */
    doInit() {
        this.stateReducer = new StateReducer(this.element, this.toggleOptions.tristate);
        return Promise.resolve({ cancelled: false });
    }
    /**
     * Binds the component to the DOM: builds the toggle DOM, binds listeners,
     * intercepts input properties, and registers the instance on the element.
     * @returns {Promise<{ cancelled: boolean }>} transition result
     */
    doAttach() {
        this.domBuilder = new DOMBuilder(this.element, this.toggleOptions, this.stateReducer.get());
        this.bindEventListeners();
        this.interceptInputProperties();
        return Promise.resolve({ cancelled: false });
    }
    /**
     * Tears down everything created by doAttach(), including the toggle DOM.
     * The original checkbox is restored to the DOM.
     * @returns {Promise<{ cancelled: boolean }>} transition result
     */
    doDispose() {
        this.unbindEventListeners();
        this.restoreInputProperties();
        this.domBuilder.destroy();
        return Promise.resolve({ cancelled: false });
    }
    /**
     * Final teardown. Defensively disposes if still attached (see decision 5b).
     * @returns {Promise<{ cancelled: boolean; reason?: string }>} transition result
     */
    doDestroy() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isAttached()) {
                try {
                    yield this.doDispose();
                }
                catch (e) {
                    return { cancelled: true, reason: e.message };
                }
            }
            delete this.element.bsToggle;
            return { cancelled: false };
        });
    }
    /**
     * Intercepts the following input properties to detect external changes:
     * - checked
     * - disabled
     * - readonly
     * - indeterminate
     * This method is used to detect changes made to the input element directly,
     * rather than through the BootstrapToggle API. It is used to maintain the
     * state of the toggle in cases where the user changes the input element
     * directly, rather than through the API.
     * @returns void
     */
    interceptInputProperties() {
        const props = ["checked", "disabled", "readOnly", "indeterminate"];
        props.forEach((prop) => {
            const descriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(this.element), prop);
            if (!(descriptor === null || descriptor === void 0 ? void 0 : descriptor.set))
                return;
            this.originalDescriptors.set(prop, descriptor);
            Object.defineProperty(this.element, prop, {
                configurable: true,
                get: () => descriptor.get.call(this.element),
                set: (value) => {
                    descriptor.set.call(this.element, value);
                    if (this.suppressExternalSync)
                        return;
                    this.onExternalChange();
                },
            });
        });
    }
    /**
     * Restores the original input properties of the toggle element.
     * This method is used to restore the original descriptors of the input properties
     * which were intercepted by the BootstrapToggle to detect external changes.
     * @returns void
     */
    restoreInputProperties() {
        this.originalDescriptors.forEach((descriptor, prop) => {
            Object.defineProperty(this.element, prop, descriptor);
        });
        this.originalDescriptors.clear();
    }
    /**
     * Binds all event listeners for the toggle component, including form reset, pointer, keyboard, and label events.
     * This method is called during the attachment phase of the component lifecycle.
     * It ensures that the toggle component responds to user interactions and external changes.
     */
    bindEventListeners() {
        this.bindFormResetListener();
        this.bindPointerEventListener();
        this.bindKeyboardEventListener();
        this.bindLabelEventListener();
    }
    /**
     * Unbinds all event listeners for the toggle component, including form reset, pointer, keyboard, and label events.
     * This method is called during the disposal phase of the component lifecycle.
     * It ensures that the toggle component no longer responds to user interactions and external changes.
     */
    unbindEventListeners() {
        this.unbindFormResetListener();
        this.unbindPointerEventListener();
        this.unbindKeyboardEventListener();
        this.unbindLabelEventListener();
    }
    bindFormResetListener() {
        const form = this.element.form;
        if (!form)
            return;
        form.addEventListener("reset", this.onFormReset);
    }
    unbindFormResetListener() {
        const form = this.element.form;
        if (!form)
            return;
        form.removeEventListener("reset", this.onFormReset);
    }
    /**
     * Binds a pointerdown event listener to the root element of the toggle.
     * The event listener is responsible for handling pointer events (e.g. mouse clicks, touch events)
     * and triggering the toggle's state change when a pointer event occurs.
     * The event listener is bound with the passive option, which means that it will not block
     * other event listeners from being triggered.
     */
    bindPointerEventListener() {
        this.domBuilder.root.addEventListener("pointerdown", this.onPointerDown, {
            passive: true,
        });
    }
    /**
     * Unbinds the pointerdown event listener from the root element of the toggle.
     * This method is responsible for unbinding the pointerdown event listener that was
     * previously bound by the bindPointerEventListener method.
     * If the event listener is not bound (i.e. this.eventsBound is false), this method does nothing.
     * @returns void
     */
    unbindPointerEventListener() {
        this.domBuilder.root.removeEventListener("pointerdown", this.onPointerDown);
    }
    /**
     * Binds a keydown event listener to the root element of the toggle.
     * The event listener is responsible for handling keydown events
     * and triggering the toggle's state change when a keydown event occurs.
     */
    bindKeyboardEventListener() {
        this.domBuilder.root.addEventListener("keydown", this.handlerKeyboardEvent, { passive: false });
    }
    /**
     * Unbinds the keydown event listener from the root element of the toggle.
     * This method is responsible for unbinding the keydown event listener that was
     * previously bound by the bindKeyboardEventListener method.
     * If the event listener is not bound (i.e. this.eventsBound is false), this method does nothing.
     * @returns void
     */
    unbindKeyboardEventListener() {
        this.domBuilder.root.removeEventListener("keydown", this.handlerKeyboardEvent);
    }
    /**
     * Binds a click event listener to all labels that are associated with the toggle's input element.
     * The event listener is responsible for handling click events and triggering the toggle's state change when a click event occurs.
     * The event listener is bound with the passive option set to false, which means that it will block other event listeners from being triggered until it has finished its execution.
     * This method is called by the constructor and is responsible for binding the event listener to the toggle's labels.
     * If the toggle's input element does not have an id (i.e. this.element.id is null or undefined), this method does nothing.
     * @returns void
     */
    bindLabelEventListener() {
        if (this.element.id) {
            document
                .querySelectorAll('label[for="' + this.element.id + '"]')
                .forEach((label) => {
                label.addEventListener("click", this.handlerLabelEvent, {
                    passive: false,
                });
            });
        }
    }
    /**
     * Unbinds the click event listener from all labels that are associated with the toggle's input element.
     * This method is responsible for unbinding the event listener that was previously bound by the bindLabelEventListener method.
     * If the toggle's input element does not have an id (i.e. this.element.id is null or undefined), this method does nothing.
     * @returns void
     */
    unbindLabelEventListener() {
        if (this.element.id) {
            document
                .querySelectorAll('label[for="' + this.element.id + '"]')
                .forEach((label) => {
                label.removeEventListener("click", this.handlerLabelEvent);
            });
        }
    }
    /**
     * Applies a toggle action to the toggle state and renders the toggle element.
     * If the action is successful, this method will render the toggle element with the new state.
     * If the silent parameter is false, this method will also trigger the change event.
     * @param action The toggle action to apply.
     * @param silent A boolean indicating whether to trigger the change event after applying the action.
     */
    apply(action, silent = false) {
        if (!this.stateReducer.do(action))
            return;
        this.suppressExternalSync = true;
        try {
            const state = this.stateReducer.get();
            this.domBuilder.render(state);
            if (!silent)
                this.trigger(action, state);
        }
        finally {
            this.suppressExternalSync = false;
        }
    }
    /**
     * Toggles the state of the toggle.
     * If the toggle is currently in the on state, it will be set to the off state.
     * If the toggle is currently in the off state, it will be set to the on state.
     * If the toggle is currently in the indeterminate state, it will be set to the on state.
     * If the silent parameter is false, this method will also trigger the change event.
     * @param silent A boolean indicating whether to trigger the change event after applying the action.
     */
    toggle(silent = false) {
        this.apply(ToggleActionType.TOGGLE, silent);
    }
    /**
     * Sets the toggle state to on.
     * If the silent parameter is false, this method will also trigger the change event.
     * @param silent A boolean indicating whether to trigger the change event after applying the action.
     */
    toggleOn(silent = false) {
        this.apply(ToggleActionType.ON, silent);
    }
    /**
     * Sets the toggle state to off.
     * If the silent parameter is false, this method will also trigger the change event.
     * @param silent A boolean indicating whether to trigger the change event after applying the action.
     */
    toggleOff(silent = false) {
        this.apply(ToggleActionType.OFF, silent);
    }
    /**
     * Sets the toggle state to indeterminate.
     * If the silent parameter is false, this method will also trigger the change event.
     * @param {boolean} silent A boolean indicating whether to trigger the change event after applying the action.
     */
    indeterminate(silent = false) {
        this.apply(ToggleActionType.INDETERMINATE, silent);
    }
    /**
     * Sets the toggle state to determinate.
     * If the silent parameter is false, this method will also trigger the change event.
     * @param {boolean} silent A boolean indicating whether to trigger the change event after applying the action.
     */
    determinate(silent = false) {
        this.apply(ToggleActionType.DETERMINATE, silent);
    }
    /**
     * Enables the toggle.
     * If the toggle is currently disabled, this method will set the toggle state to enabled.
     * If the silent parameter is false, this method will also trigger the change event.
     * @param {boolean} silent A boolean indicating whether to trigger the change event after applying the action.
     * @returns void
     */
    enable(silent = false) {
        this.apply(ToggleActionType.ENABLE, silent);
    }
    /**
     * Disables the toggle.
     * If the toggle is currently enabled, this method will set the toggle state to disabled.
     * If the silent parameter is false, this method will also trigger the change event.
     * @param {boolean} silent A boolean indicating whether to trigger the change event after applying the action.
     */
    disable(silent = false) {
        this.apply(ToggleActionType.DISABLE, silent);
    }
    /**
     * Sets the toggle state to readonly.
     * If the toggle is currently disabled or enabled, this method will set the toggle state to readonly.
     * If the silent parameter is false, this method will also trigger the change event.
     * @param {boolean} silent A boolean indicating whether to trigger the change event after applying the action.
     * @returns void
     */
    readonly(silent = false) {
        this.apply(ToggleActionType.READONLY, silent);
    }
    /**
     * Synchronizes the toggle state with the input element and renders the toggle.
     */
    update() {
        this.suppressExternalSync = true;
        try {
            this.stateReducer.sync(this.element);
            this.domBuilder.render(this.stateReducer.get());
        }
        finally {
            this.suppressExternalSync = false;
        }
    }
    /**
     * Triggers the change event on the toggle's input element and the appropriate toggle event.
     * This method is called after a toggle action is applied to notify listeners of the state change.
     * @param {ToggleActionType} action The toggle action that was applied.
     * @param {ToggleState} state The state of the toggle once the action was applied.
     */
    trigger(action, state) {
        this.element.dispatchEvent(new Event("change", { bubbles: true }));
        const eventName = this.getEventForAction(action, state);
        this.emit(eventName, { state });
    }
    /**
     * Returns the corresponding toggle event for the given toggle action and state.
     * This method is used to determine which toggle event to trigger after a toggle action is applied.
     * @param {ToggleActionType} action The toggle action that was applied.
     * @param {ToggleState} state The previous state of the toggle before the action was applied.
     * @returns {ToggleEvents} The corresponding toggle event for the given toggle action and state.
     */
    getEventForAction(action, state) {
        switch (action) {
            case ToggleActionType.ON:
                return ToggleEvents.ON;
            case ToggleActionType.OFF:
                return ToggleEvents.OFF;
            case ToggleActionType.INDETERMINATE:
                return ToggleEvents.MIXED;
            case ToggleActionType.ENABLE:
                return ToggleEvents.ENABLED;
            case ToggleActionType.DISABLE:
                return ToggleEvents.DISABLED;
            case ToggleActionType.READONLY:
                return ToggleEvents.READONLY;
            case ToggleActionType.DETERMINATE:
            case ToggleActionType.TOGGLE:
            case ToggleActionType.NEXT:
                return this.getValueEvent(state);
        }
    }
    /**
     * Returns the corresponding toggle event for the given toggle state.
     * This method is used to determine which toggle event to trigger after a toggle action is applied.
     * @param {ToggleState} state The previous state of the toggle before the action was applied.
     * @returns {ToggleEvents} The corresponding toggle event for the given toggle state.
     */
    getValueEvent(state) {
        switch (state.value) {
            case ToggleStateValue.ON:
                return ToggleEvents.ON;
            case ToggleStateValue.OFF:
                return ToggleEvents.OFF;
            case ToggleStateValue.MIXED:
                return ToggleEvents.MIXED;
        }
    }
    /**
     * Rebuilds the toggle in-place, preserving instance identity.
     * Emits `toggle:disposed` and `toggle:attached` lifecycle events.
     *
     * Sequence:
     *   1. dispose()  → doDispose() unbinds listeners, restores props, tears down DOM
     *   2. re-resolve options from element (may have changed)
     *   3. attach()   → doAttach() rebuilds DOM and re-binds listeners
     *
     * @returns {Promise<void>} resolves when re-attached.
     */
    rerender() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.dispose();
            this.toggleOptions = OptionResolver.resolve(this.element, this.userOptions);
            this.stateReducer = new StateReducer(this.element, this.toggleOptions.tristate);
            yield this.attach();
        });
    }
}
